import { Alert, Button, Stack, Text, TextInput } from "@mantine/core";
import { IconBarcode, IconCircleCheck } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaptureSheet } from "../../capture";
import { BarcodeScanner } from "../../capture/BarcodeScanner";
import { normaliseSerial } from "../../capture/barcode";
import { CustomerPicker } from "../../components/CustomerPicker";
import { ModelPicker } from "../../components/ModelPicker";
import { NumField, SectionCard } from "../../components/ui";
import { api, type Customer } from "../../lib/api";
import { Page } from "../../shell/Page";

/**
 * Step 1: collection (REQ-SVC-1).
 *
 * THE ORDER OF THE FIELDS IS THE ORDER OF THE WORK
 *
 * Customer, then MODEL, then serial number, then quantity. Set by the
 * business after testing: the technician arrives, confirms whose battery it
 * is, reads the model off the label, then reads the long serial number off
 * the same label - or scans it, which is the slow step and therefore last of
 * the two.
 *
 * The MODEL is a catalogue product, and the four category levels - company,
 * main category, category, sub category - are DERIVED from it and shown
 * read-only. They replaced four dropdowns the technician used to work down
 * themselves; four levels nobody chooses are four levels nobody can get
 * wrong.
 *
 * WHAT WAS REMOVED, AND WHAT THAT COSTS
 *
 * "Not a battery we sell" is gone, on the business's instruction that it
 * never happens. Every battery coming back is one Harsha Agencies sold, so
 * every one has a model. The consequence is real and worth knowing: a battery
 * whose model is genuinely not in the catalogue cannot be collected until the
 * Store Manager adds it. The picker says so rather than leaving the
 * technician guessing why nothing matches.
 *
 * The free-text description is gone too, for the same reason - the model says
 * what the battery is. Jobs collected before this release keep theirs, and
 * every screen still shows it when it is there.
 *
 * The SERIAL NUMBER is unchanged and still the identifier: it is what a
 * warranty claim is made against, what the manufacturer asks for, and the
 * only way to answer "has this battery been in before". The model says what
 * KIND of battery it is; the serial says WHICH one.
 */
export function CollectBattery() {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer>();
  const [productId, setProductId] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [itemCount, setItemCount] = useState<number | string>(1);
  const [collected, setCollected] = useState<{ reference: string; id: string }>();

  const count = Number(itemCount);
  const countValid = Number.isInteger(count) && count > 0;
  const serialValid = serialNumber.trim().length >= 3;

  if (collected) {
    return (
      <Page title="Collected" subtitle={collected.reference}>
        <Alert
          variant="light"
          color="teal"
          radius="md"
          icon={<IconCircleCheck size={20} stroke={1.8} />}
          title={`Logged as ${collected.reference}`}
        >
          The workshop will confirm it when it arrives.
        </Alert>
        <Stack gap="sm">
          <Button size="md" fullWidth onClick={() => setCollected(undefined)}>
            Collect another
          </Button>
          <Button
            variant="default"
            size="md"
            fullWidth
            onClick={() => navigate(`/service/jobs/${collected.id}`)}
          >
            View this job
          </Button>
        </Stack>
      </Page>
    );
  }

  return (
    <Page title="Collect a battery" subtitle="Stream B · step 1 of 4" back="/service">
      <CaptureSheet
        kind="COLLECTION"
        title="Pickup"
        description="Photo and location prove the collection. Both are required."
        submitLabel="Log the collection"
        photoLabel="Battery photo"
        disabled={!customer || productId === "" || !serialValid || !countValid}
        onSubmit={async (capture) => {
          const { job } = await api.collectServiceJob({
            customerId: customer!.id,
            productId,
            serialNumber: normaliseSerial(serialNumber),
            itemCount: count,
            capture: {
              ...(capture.gps ? { gps: capture.gps } : {}),
              ...(capture.photoDataUrl ? { photoDataUrl: capture.photoDataUrl } : {}),
              ...(capture.notes ? { notes: capture.notes } : {}),
              clientCapturedAt: capture.clientCapturedAt,
            },
          });
          setCollected({ reference: job.reference, id: job.id });
        }}
        onCancel={() => navigate("/service")}
      >
        <Stack gap="md">
          <SectionCard title="Customer" required>
            <CustomerPicker value={customer} onChange={setCustomer} />
          </SectionCard>

          {/* The model, and the four levels it fills in. Before the serial
              number because that is the order the work happens in: the model
              is short and on the front of the label, the serial number is
              long and is the one worth scanning. */}
          <ModelPicker value={productId} onChange={setProductId} />

          <SectionCard title="The battery itself">
            <Stack gap="sm">
              {scanning ? (
                <BarcodeScanner
                  onRead={(value) => {
                    setSerialNumber(value);
                    setScanned(true);
                    setScanning(false);
                  }}
                  onClose={() => setScanning(false)}
                />
              ) : (
                <>
                  {/* A real <label> via the input's own `label` prop, not a
                      heading beside it. As an <h3> this named nothing: the
                      field had no accessible name at all, so a screen reader
                      announced an unlabelled text box and "Serial number" was
                      decoration. A browser drive found it by failing to
                      locate the field by its own label. */}
                  <TextInput
                    id="serial-number"
                    label="Serial number"
                    withAsterisk
                    inputMode="text"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={120}
                    placeholder="Scan it, or type what is stamped on the battery"
                    value={serialNumber}
                    onChange={(event) => {
                      setSerialNumber(event.currentTarget.value);
                      setScanned(false);
                    }}
                    error={
                      !serialValid && serialNumber.trim().length > 0
                        ? "That looks too short for a serial number."
                        : undefined
                    }
                    description="Scanning and typing record exactly the same thing. If the label is damaged or will not read, type it."
                    className="num"
                  />
                  <Button
                    variant="default"
                    size="md"
                    fullWidth
                    leftSection={<IconBarcode size={18} stroke={1.8} />}
                    onClick={() => setScanning(true)}
                  >
                    Scan the barcode
                  </Button>
                  {scanned && (
                    <Alert variant="light" color="teal" radius="md" p="sm">
                      <Text fz="sm">
                        Scanned <span className="num">{serialNumber}</span>. Check it
                        against the battery before logging the collection.
                      </Text>
                    </Alert>
                  )}
                </>
              )}

              <NumField
                id="item-count"
                label="Quantity"
                min={1}
                step={1}
                value={itemCount}
                onChange={setItemCount}
                error={!countValid ? "Enter 1 or more." : undefined}
              />
            </Stack>
          </SectionCard>
        </Stack>
      </CaptureSheet>
    </Page>
  );
}
